import { useEffect, useMemo, useState } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { useCounterReadOnly } from '@/lib/useCounterSession';
import DutyStrip from './DutyStrip';
import { fitSheetToOnePage, releaseSheet } from '@/lib/printSheet';
import { startOfWeek, format, addDays, addWeeks, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  FileText, ChevronLeft, ChevronRight, RefreshCw,
  Ban, CalendarOff, Clock, Info, CheckCircle2, Plus, Trash2, X, Wand2, GripVertical,
  Printer, ArrowLeftRight, RotateCcw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useEmployees, useSchedules, useGenerateSchedules, useUpsertSchedule, useDeleteSchedule,
  useUnavailabilitiesRange, useHolidays, useCoverageRange, usePharmacySettings,
  useCreateAbsence, useSetUnavailability, useVariableStaff, useSites, useSwitchScheduleSite,
} from '@/api/resources';
import type { DayCoverage, CoverageStatus, Site } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import type { Schedule, ScheduleStatus, AbsenceType } from '@/types';
import { absenceMeta, ABSENCE_META, ABSENCE_TYPES, ABSENCE_CELL_MUTED } from '@/lib/absenceTypes';
import { positionStyle, byPositionThenName, POSITION_LEGEND } from '@/lib/positions';
import { isEmployedBetween, isEmployedOn, contractNote } from '@/lib/employment';
import {
  computeDailyHours, slotLabel, isWorkingDay, closedByWeekday as closed, weekDayCount,
  type Slot,
} from '@/lib/schedule';
import VariableFillModal from './VariableFillModal';

// Le dimanche n'apparaît que si l'officine ouvre ce jour-là (`weekDayCount`).
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Repli par statut si un jour d'absence n'a pas de type précis (absenceType null).
// `dot` = pastille de couleur : seule trace du motif dans une case effacée.
const STATUS_META: Partial<Record<ScheduleStatus, { label: string; dot: string }>> = {
  LEAVE:     { label: 'Congé',            dot: '#64748b' },
  SICK:      { label: 'Arrêt maladie',    dot: '#dc2626' },
  ACCIDENT:  { label: 'Accident travail', dot: '#dc2626' },
  MATERNITY: { label: 'Maternité',        dot: '#db2777' },
};

const ABSENCE_STATUSES: ScheduleStatus[] = ['LEAVE', 'SICK', 'ACCIDENT', 'MATERNITY'];


/**
 * M9-1 — Le créneau saisi déborde-t-il de la fenêtre d'ouverture ? Le planning
 * ne rabote plus rien : certains postes (ménage, magasinier, préparation de
 * commandes) travaillent volontairement hors ouverture. On se contente de le
 * signaler, pour qu'un horaire inattendu ne passe pas pour un bug.
 * Miroir de `isOutsideOpening` côté backend (`schedule-generator.ts`).
 */
function isOutsideOpening(
  start: string | null | undefined,
  end: string | null | undefined,
  open: string | null | undefined,
  close: string | null | undefined
): boolean {
  if (!start || !end) return false; // pas de créneau : rien à signaler
  if (open == null || close == null) return true; // demi-journée fermée
  return start < open || end > close; // "HH:MM" se compare bien en texte
}

interface DayStat { present: number; absent: number }
type UnavailInfo = { type: AbsenceType; startTime?: string | null; endTime?: string | null };

/** Absent au sens « pas là de la journée » : absence RH validée ou indispo pleine journée. */
const isAbsentDay = (s: Schedule | undefined, u: UnavailInfo | undefined): boolean =>
  (!!s && (s.absenceType != null || ABSENCE_STATUSES.includes(s.status))) ||
  (!!u && !u.startTime);

const COVERAGE_META: Record<CoverageStatus, { dot: string; label: string }> = {
  COVERED:   { dot: 'bg-emerald-500', label: 'Effectifs couverts' },
  PARTIAL:   { dot: 'bg-amber-500',   label: "Sous l'effectif cible" },
  UNCOVERED: { dot: 'bg-red-500',     label: "Sous l'effectif minimum" },
  CLOSED:    { dot: 'bg-slate-300',   label: 'Fermé' },
};

/** Effectif du jour, lisible d'un coup d'œil : combien de présents, combien d'absents. */
function DayStats({ cov, stats }: { cov: DayCoverage | undefined; stats: DayStat }) {
  const closed = cov?.worst === 'CLOSED';
  const meta = cov && !closed ? COVERAGE_META[cov.worst] : null;
  const alerts = cov
    ? [
        ...cov.morning.alerts.map((a) => `Matin — ${a.message}`),
        ...cov.afternoon.alerts.map((a) => `A-M — ${a.message}`),
      ]
    : [];
  const title = meta
    ? (alerts.length > 0 ? `${meta.label}\n${alerts.join('\n')}` : meta.label)
    : undefined;
  return (
    <div className="mt-1 flex items-center justify-center gap-1.5" title={title}>
      {meta && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />}
      <span className="text-[10px] font-semibold text-slate-700 tabular-nums">
        {stats.present} présent{stats.present > 1 ? 's' : ''}
      </span>
      {stats.absent > 0 && (
        <span className="text-[10px] text-slate-400 tabular-nums">· {stats.absent} abs.</span>
      )}
    </div>
  );
}

export default function PlanningPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekStart = useMemo(
    () => addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset),
    [weekOffset]
  );
  // Largeur de la semaine : lundi→samedi, plus le dimanche si l'officine ouvre
  // ce jour-là. Tant que les horaires ne sont pas chargés on reste à 6 jours,
  // ce qui est le cas de la quasi-totalité des officines.
  const { data: pharmacy } = usePharmacySettings();
  // Les officines du groupe, pour pouvoir nommer une demi-journée qui se fait
  // ailleurs. En mono-site la liste tient en une ligne et rien ne s'affiche.
  const { data: sitesData } = useSites();
  const activeSiteId = sitesData?.activeSiteId;
  const siteNameById = useMemo(
    () => new Map((sitesData?.sites ?? []).map((s) => [s.id, s.name])),
    [sitesData],
  );
  const dayCount = useMemo(() => weekDayCount(pharmacy?.openingHours), [pharmacy]);
  const gridCols = `180px repeat(${dayCount}, 1fr) 80px`;

  const weekEnd = useMemo(() => addDays(weekStart, dayCount - 1), [weekStart, dayCount]);
  const fromIso = format(weekStart, 'yyyy-MM-dd');
  const toIso = format(weekEnd, 'yyyy-MM-dd');

  const { data: employeesRaw } = useEmployees();
  // M9-2 — Regroupés par poste : pharmaciens en haut, puis préparateurs,
  // étudiants, autres. L'API trie déjà ainsi, on le réaffirme ici pour que
  // l'ordre des lignes colle toujours aux couleurs.
  // Les salariés dont le contrat ne croise pas la semaine affichée n'y ont pas
  // de ligne : une recrue du 7 septembre n'apparaît pas dans le planning d'août,
  // et un CDD terminé sort de la grille le lendemain de son dernier jour.
  const employees = useMemo(
    () =>
      employeesRaw
        ? employeesRaw
            .filter((e) => isEmployedBetween(e, weekStart, weekEnd))
            .sort(byPositionThenName)
        : employeesRaw,
    [employeesRaw, weekStart, weekEnd]
  );
  // `elsewhere` : une journée basculée ENTIÈREMENT dans une autre officine
  // s'écrit « → Palais » ici, au lieu de laisser une case vide.
  const { data: schedules } = useSchedules(fromIso, toIso, undefined, { elsewhere: true });
  // Début d'utilisation du logiciel : avant, rien n'est planifié ni compté.
  const usageStart: string | null = pharmacy?.usageStartDate ? String(pharmacy.usageStartDate).slice(0, 10) : null;
  const { data: unavailabilities } = useUnavailabilitiesRange(fromIso, toIso);
  const { data: holidays } = useHolidays();
  const { data: coverage } = useCoverageRange(fromIso, toIso);
  // Poste comptoir : la semaine s'affiche, elle ne se modifie pas. Le serveur
  // refuse déjà toute écriture (liste blanche du mode comptoir) ; ici on retire
  // les gestes qui n'aboutiraient pas, plutôt que de les laisser échouer.
  const readOnly = useCounterReadOnly();
  const generateMut = useGenerateSchedules();
  const variableStaff = useVariableStaff(!readOnly);
  const upsertMut = useUpsertSchedule();
  const deleteMut = useDeleteSchedule();
  const switchSiteMut = useSwitchScheduleSite();
  const [editor, setEditor] = useState<
    { empId: string; empName: string; dateStr: string; label: string; s?: Schedule; defaultTab: EditorTab; multiSite: boolean } | null
  >(null);
  // Message d'échec du serveur (créneau refusé, salarié hors officine…). Il
  // était jeté : la fenêtre restait ouverte sans rien dire, et le titulaire
  // concluait « ça ne marche pas ».
  const [editorError, setEditorError] = useState<string | null>(null);

  function openEditor(
    empId: string, empName: string, dateStr: string, label: string,
    s?: Schedule, defaultTab: EditorTab = 'absence'
  ) {
    if (readOnly) return; // poste comptoir : consultation seule
    if (s?.locked) return; // semaine validée : non modifiable ici
    setEditorError(null);
    const emp = employees?.find((e) => e.id === empId);
    // Une journée faite ailleurs s'ouvre sur l'onglet « Officine » : c'est la
    // seule chose qu'on puisse y faire depuis ici, sans retaper ses heures.
    setEditor({
      empId, empName, dateStr, label, s,
      defaultTab: s?.elsewhere ? 'site' : defaultTab,
      multiSite: !!(emp as any)?.multiSite,
    });
  }

  // --------------------------------------------------------------------------
  // Glisser-déposer : recopier une journée sur une autre
  // --------------------------------------------------------------------------
  // Régler des horaires case par case est fastidieux quand la même journée se
  // répète. On attrape une journée travaillée et on la lâche sur une autre : ses
  // horaires y sont recopiés. La journée d'origine ne bouge pas — c'est une
  // COPIE, pas un déplacement.
  //
  // **Volontairement borné à une seule ligne** : on ne peut pas lâcher sur un
  // autre salarié. Croiser deux salariés ferait passer par la case « horaires du
  // voisin » et ses règles propres (contrat, compétences, repos), pour un geste
  // qu'on fait vite et sans regarder. Une ligne, une personne.
  const [drag, setDrag] = useState<
    { empId: string; dateStr: string; label: string; slots: ShiftSlots } | null
  >(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  // Le drop se joue hors de toute fenêtre : sans ce bandeau, un refus du serveur
  // (jour verrouillé, absence, repos fixe) n'aurait nulle part où s'afficher.
  const [dropFlash, setDropFlash] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  // Le bandeau nomme un jour précis : le garder en changeant de semaine le
  // ferait parler d'une case qui n'est plus à l'écran.
  useEffect(() => { setDropFlash(null); setDrag(null); setDragOverKey(null); }, [weekOffset]);

  // La feuille est réduite juste avant que le dialogue s'ouvre, pour tenir sur
  // une seule page. Branché sur l'événement du navigateur plutôt que sur le
  // bouton : le Ctrl+P de l'utilisateur en profite aussi.
  useEffect(() => {
    window.addEventListener('beforeprint', fitSheetToOnePage);
    window.addEventListener('afterprint', releaseSheet);
    return () => {
      window.removeEventListener('beforeprint', fitSheetToOnePage);
      window.removeEventListener('afterprint', releaseSheet);
      releaseSheet();
    };
  }, []);

  /** Une journée n'est attrapable que si elle a des heures à recopier. */
  function dragSlotsOf(s: Schedule | undefined): ShiftSlots | null {
    if (readOnly) return null; // rien n'est attrapable au comptoir
    if (!s || s.status !== 'WORK' || s.locked) return null;
    const slots: ShiftSlots = {
      morningStart: s.morningStart ?? null,
      morningEnd: s.morningEnd ?? null,
      afternoonStart: s.afternoonStart ?? null,
      afternoonEnd: s.afternoonEnd ?? null,
    };
    const hasHours =
      (slots.morningStart && slots.morningEnd) || (slots.afternoonStart && slots.afternoonEnd);
    return hasHours ? slots : null;
  }

  /**
   * Quitter une case n'éteint son surlignage que si on en sort VRAIMENT.
   * `dragleave` part aussi quand le curseur passe sur un enfant de la case (les
   * deux demi-journées) : sans ce filtre, la cible clignotait pendant qu'on la
   * survolait.
   */
  function leaveCell(e: React.DragEvent<HTMLDivElement>, key: string) {
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    setDragOverKey((k) => (k === key ? null : k));
  }

  /** Cible valide : même salarié, autre jour, et jour non verrouillé. */
  function isValidDrop(
    source: { empId: string; dateStr: string } | null,
    empId: string, dateStr: string, target: Schedule | undefined
  ): boolean {
    if (readOnly) return false;
    return !!source && source.empId === empId && source.dateStr !== dateStr && !target?.locked;
  }
  const canDropOn = (empId: string, dateStr: string, target: Schedule | undefined) =>
    isValidDrop(drag, empId, dateStr, target);

  async function handleDrop(empId: string, dateStr: string, label: string, target: Schedule | undefined) {
    // La source est lue AVANT de vider l'état : le contrôle porte sur la journée
    // réellement attrapée, pas sur ce que `drag` vaudra au rendu suivant.
    const source = drag;
    setDrag(null);
    setDragOverKey(null);
    if (!isValidDrop(source, empId, dateStr, target) || !source) return;
    setDropFlash(null);
    try {
      await upsertMut.mutateAsync({
        employeeId: empId,
        date: dateStr,
        status: 'WORK',
        ...source.slots,
      });
      setDropFlash({ kind: 'ok', text: `${source.label} recopié sur ${label}.` });
    } catch (e) {
      // Mêmes refus que la saisie à la main (absence, repos fixe, semaine
      // validée…) : le serveur les explique en français, on les affiche tels quels.
      setDropFlash({ kind: 'error', text: e instanceof Error ? e.message : 'Copie impossible.' });
    }
  }

  // Comblement des creux par le personnel en horaires variables. On ouvre
  // l'apercu sur la semaine affichee : le titulaire choisit qui recalculer.
  const [fillOpen, setFillOpen] = useState(false);

  async function handleGenerate() {
    // Matérialise le planning (reflet des disponibilités) de la semaine affichée
    // jusqu'à ~6 mois plus loin. Ne modifie rien manuellement.
    const to = format(addWeeks(weekStart, 26), 'yyyy-MM-dd');
    await generateMut.mutateAsync({ from: fromIso, to });
  }

  const scheduleMap = useMemo(() => {
    const map = new Map<string, Schedule>();
    schedules?.forEach((s) => map.set(`${s.employeeId}|${s.date.slice(0, 10)}`, s));
    return map;
  }, [schedules]);

  const unavailMap = useMemo(() => {
    const map = new Map<string, UnavailInfo>();
    unavailabilities?.forEach((u) => {
      map.set(`${u.employeeId}|${u.date.slice(0, 10)}`, { type: u.type, startTime: u.startTime, endTime: u.endTime });
    });
    return map;
  }, [unavailabilities]);

  // `isOpen` vient de Paramètres › Jours fériés (« Ouvert ? », non par défaut).
  // Un férié FERMÉ se lit comme un jour de fermeture : colonne grisée.
  const holidayMap = useMemo(() => {
    const map = new Map<string, { name: string; isOpen: boolean }>();
    holidays?.forEach((h: any) => map.set(String(h.date).slice(0, 10), { name: h.name, isOpen: !!h.isOpen }));
    return map;
  }, [holidays]);

  const coverageMap = useMemo(() => {
    const map = new Map<string, DayCoverage>();
    coverage?.forEach((d) => map.set(d.date, d));
    return map;
  }, [coverage]);

  const dayKeys = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd')),
    [weekStart, dayCount]
  );

  // Effectif par jour — le chiffre que le titulaire cherche en parcourant la semaine.
  const dayStats = useMemo<DayStat[]>(() => {
    return dayKeys.map((dk) => {
      let present = 0;
      let absent = 0;
      employees?.forEach((emp) => {
        const s = scheduleMap.get(`${emp.id}|${dk}`);
        const u = unavailMap.get(`${emp.id}|${dk}`);
        if (isAbsentDay(s, u)) absent++;
        else if (isWorkingDay(s)) present++;
      });
      return { present, absent };
    });
  }, [dayKeys, employees, scheduleMap, unavailMap]);

  // Salariés absents TOUTE la semaine → ligne entière estompée (on ne les cherche
  // pas du regard). On exige au moins un jour d'absence avéré pour ne pas
  // estomper un salarié dont le planning n'a simplement pas encore été généré.
  const awayAllWeek = useMemo(() => {
    const set = new Set<string>();
    employees?.forEach((emp) => {
      let works = false;
      let away = false;
      dayKeys.forEach((dk) => {
        const s = scheduleMap.get(`${emp.id}|${dk}`);
        const u = unavailMap.get(`${emp.id}|${dk}`);
        if (isWorkingDay(s)) works = true;
        if (isAbsentDay(s, u)) away = true;
      });
      if (!works && away) set.add(emp.id);
    });
    return set;
  }, [employees, dayKeys, scheduleMap, unavailMap]);

  const closedByWeekday = useMemo(() => closed(pharmacy?.openingHours), [pharmacy]);

  // Fenêtre d'ouverture par jour : sert à repérer les créneaux qui débordent
  // (M9-1). Le planning ne les corrige plus — ménage, magasinier et préparation
  // de commandes travaillent volontairement hors ouverture — il les signale.
  const openingByWeekday = useMemo(() => {
    const map = new Map<number, { morningOpen: string | null; morningClose: string | null; afternoonOpen: string | null; afternoonClose: string | null }>();
    (pharmacy?.openingHours ?? []).forEach((o: any) => {
      map.set(o.dayOfWeek, {
        morningOpen: o.closed ? null : o.morningOpen ?? null,
        morningClose: o.closed ? null : o.morningClose ?? null,
        afternoonOpen: o.closed ? null : o.afternoonOpen ?? null,
        afternoonClose: o.closed ? null : o.afternoonClose ?? null,
      });
    });
    return map;
  }, [pharmacy]);

  const isWeekB = getISOWeek(weekStart) % 2 === 1;
  const workedWeekdaysByEmp = useMemo(() => {
    const map = new Map<string, Set<number>>();
    employees?.forEach((emp) => {
      const weekType = emp.scheduleMode === 'ALTERNATING' && isWeekB ? 'B' : 'A';
      const worked = new Set<number>();
      (emp.weeklyTemplates ?? []).forEach((t) => { if (t.works && t.weekType === weekType) worked.add(t.dayOfWeek); });
      map.set(emp.id, worked);
    });
    return map;
  }, [employees, isWeekB]);

  return (
    <div className="flex flex-col h-full">
      <div className="no-print border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">Planning semaine <PageHelp topic="planning" /></h1>
            <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
              Semaine du {format(weekStart, 'd MMM', { locale: fr })} au {format(weekEnd, 'd MMM yyyy', { locale: fr })}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            <button onClick={() => setWeekOffset((o) => o - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Semaine précédente"><ChevronLeft size={16} /></button>
            <button onClick={() => setWeekOffset(0)} className="px-2 py-1 rounded-lg hover:bg-slate-100 text-[11px] font-medium text-slate-600">Aujourd'hui</button>
            <button onClick={() => setWeekOffset((o) => o + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600" title="Semaine suivante"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Hors de la bascule `readOnly` : imprimer ne modifie rien, et le
              poste comptoir est justement l'écran d'où l'équipe sort la semaine
              pour l'afficher dans l'officine. */}
          <Button
            variant={readOnly ? 'primary' : 'default'}
            icon={Printer}
            onClick={() => window.print()}
            title="Imprimer la semaine affichée, pour l'afficher dans l'officine"
          >
            Imprimer
          </Button>
          {readOnly ? (
            <span className="text-[11.5px] text-slate-500 self-center">Consultation</span>
          ) : (
          <>
          <Button variant="ghost" icon={RefreshCw} loading={generateMut.isPending} onClick={handleGenerate} title="Matérialise le planning des disponibilités jusqu'à ~6 mois">Générer</Button>
          {variableStaff.data && variableStaff.data.length > 0 && (
            <Button
              variant="default"
              icon={Wand2}
              onClick={() => setFillOpen(true)}
              title="Fait venir le personnel en horaires variables sur les creux de la semaine"
            >
              Combler les trous
            </Button>
          )}
          <Link to="/validation"><Button variant="default" icon={CheckCircle2}>Valider la semaine</Button></Link>
          <Link to="/shuttle"><Button variant="primary" icon={FileText}>Fiche navette</Button></Link>
          </>
          )}
        </div>
      </div>

      {/* Bandeau : le planning est un reflet des disponibilités */}
      <div className="no-print bg-primary-bg/40 border-b border-primary/15 px-8 py-2 text-[11.5px] text-slate-600 flex items-center gap-2">
        <Info size={13} className="text-primary flex-shrink-0" />
        {/* La phrase dans UN seul enfant : sans ce span, chaque fragment de
            texte et chaque <strong> devient un élément flex et la ligne
            s'étale en colonnes espacées au lieu de se lire comme une phrase. */}
        <span>
          Le planning est le <strong>reflet des disponibilités</strong> des salariés. Cliquez une case pour <strong>déclarer une absence</strong> ou ajouter des heures ponctuelles, ou <strong>faites glisser une journée sur une autre</strong> pour y recopier ses horaires (même salarié). Les <strong>horaires habituels</strong> (semaine type) restent réglés depuis la <strong>fiche du salarié</strong>.
        </span>
      </div>

      {/* Résultat du dernier glisser-déposer. Le geste se fait hors de toute
          fenêtre : sans ce bandeau, un refus n'aurait nulle part où s'afficher. */}
      {dropFlash && (
        <div
          className={`no-print px-8 py-2 text-[11.5px] flex items-center gap-2 border-b ${
            dropFlash.kind === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-danger/5 border-danger/25 text-danger'
          }`}
        >
          {/* Le bandeau sert aussi au « Basculer à la pharmacie… » : la
              fenêtre se ferme sur un succès, c'est ici qu'on le confirme. */}
          {dropFlash.kind === 'ok' ? <CheckCircle2 size={13} className="flex-shrink-0" /> : <Ban size={13} className="flex-shrink-0" />}
          <span className="flex-1">{dropFlash.text}</span>
          <button type="button" onClick={() => setDropFlash(null)} className="p-0.5 rounded hover:bg-black/5" title="Fermer">
            <X size={13} />
          </button>
        </div>
      )}

      {/* `edt-page` ne sert qu'à l'impression : c'est la PAGE, quand `#edt-print`
          est la feuille qu'on y réduit. Voir le bloc @media print d'index.css. */}
      <div className="edt-page flex-1 overflow-auto p-6">
       <div id="edt-print">
        {/* En-tête de la FEUILLE. À l'écran, la barre d'outils le dit déjà ;
            imprimé, c'est la seule chose qui date le papier accroché au mur. */}
        <div className="print-only mb-2">
          <div className="text-[15px] font-semibold">
            {pharmacy?.name ? `${pharmacy.name} — ` : ''}Planning de la semaine
          </div>
          <div className="text-[11px] text-slate-600 tabular-nums">
            Semaine {getISOWeek(weekStart)} · du {format(weekStart, 'EEEE d MMMM', { locale: fr })} au{' '}
            {format(weekEnd, 'EEEE d MMMM yyyy', { locale: fr })}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" style={{ minWidth: 260 + dayCount * 140 }}>
          <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: gridCols }}>
            <div className="px-3 py-2.5 text-[11px] font-medium text-slate-600 uppercase tracking-wider border-r border-slate-200">Salarié</div>
            {DAYS_FULL.slice(0, dayCount).map((d, i) => {
              const date = addDays(weekStart, i);
              const dayKey = format(date, 'yyyy-MM-dd');
              const isToday = dayKey === format(new Date(), 'yyyy-MM-dd');
              const holiday = holidayMap.get(dayKey);
              const holidayClosed = !!holiday && !holiday.isOpen;
              const cov = coverageMap.get(dayKey);
              return (
                <div key={d} className={`px-2 py-2.5 text-center border-r border-slate-200 ${holidayClosed ? 'bg-slate-200/70' : holiday ? 'bg-amber-50' : isToday ? 'bg-primary-bg/50' : ''}`}>
                  <div className={`text-[11px] font-medium ${holidayClosed ? 'text-slate-500' : isToday ? 'text-primary' : ''}`}>{d}</div>
                  <div className="text-[10px] text-slate-500 tabular-nums">{format(date, 'dd/MM')}</div>
                  <DayStats cov={cov} stats={dayStats[i] ?? { present: 0, absent: 0 }} />
                  {holidayClosed ? (
                    <div className="mt-1 text-[9px] font-semibold text-slate-500 leading-tight truncate" title={`Férié, officine fermée : ${holiday.name} (Paramètres → Jours fériés)`}>
                      Férié fermé · {holiday.name}
                    </div>
                  ) : holiday ? (
                    <div className="mt-1 text-[9px] font-medium text-amber-700 leading-tight truncate" title={`Férié, officine ouverte : ${holiday.name}`}>🎉 {holiday.name}</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-0.5 mt-1 text-[9px] text-slate-400 uppercase"><div>Matin</div><div>A-M</div></div>
                  )}
                </div>
              );
            })}
            <div className="px-2 py-2.5 text-center text-[11px] font-medium text-slate-600 uppercase">Total</div>
          </div>

          {employees?.map((emp, idx) => {
            const isTemp = emp.contractType === 'REMPLACANT' || emp.contractType === 'INTERIM';
            const isAway = awayAllWeek.has(emp.id);
            // M9-2 — La couleur du POSTE l'emporte sur la couleur personnelle
            // du salarié (`emp.color`, qui reste l'avatar ailleurs) : deux codes
            // couleur concurrents sur le même écran ne se lisent pas.
            const posStyle = positionStyle(emp.position);
            let weekHours = 0;
            return (
              <div
                key={emp.id}
                title={isAway ? 'Absent toute la semaine' : undefined}
                className={`grid border-b border-slate-100 ${isTemp ? 'bg-[#fffaf5]' : idx % 2 === 1 ? 'bg-slate-50/40' : ''} ${isAway ? 'opacity-50' : ''}`}
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className="px-3 py-2 border-r border-slate-200 flex items-center gap-2">
                  <div className="w-1 h-9 rounded-full" style={{ backgroundColor: posStyle.bg }} />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium truncate">{emp.firstName} {emp.lastName}</div>
                    <div className="text-[10px] text-slate-500 truncate capitalize">{emp.position.replace(/_/g, ' ').toLowerCase()}</div>
                    {/* Semaine à cheval sur l'arrivée ou la fin du contrat : le
                        dire, sinon les cases barrées passent pour un bug. */}
                    {contractNote(emp, weekStart, weekEnd) && (
                      <div className="text-[9.5px] text-amber-700 truncate normal-case">
                        {contractNote(emp, weekStart, weekEnd)}
                      </div>
                    )}
                  </div>
                </div>
                {Array.from({ length: dayCount }).map((_, dayIdx) => {
                  const date = addDays(weekStart, dayIdx);
                  const dayKey = format(date, 'yyyy-MM-dd');
                  const s = scheduleMap.get(`${emp.id}|${dayKey}`);
                  const unavail = unavailMap.get(`${emp.id}|${dayKey}`);
                  const isFullDayUnavail = !!unavail && !unavail.startTime;
                  const isPartialUnavail = !!(unavail?.startTime && unavail?.endTime);
                  weekHours += computeDailyHours(s);

                  // 0. Hors contrat : le salarié n'est pas encore arrivé (ou est
                  //    déjà parti). Ce n'est ni du repos ni une absence — la case
                  //    est simplement hors sujet, et non cliquable.
                  //    Idem avant le début d'utilisation du logiciel : personne
                  //    ne tenait le planning ici, rien n'y est compté.
                  const beforeUsage = !!usageStart && dayKey < usageStart;
                  if (beforeUsage || !isEmployedOn(emp, date)) {
                    const before = !!emp.hireDate && dayKey < emp.hireDate.slice(0, 10);
                    return (
                      <div key={dayIdx} className="p-1 border-r border-slate-100">
                        <div
                          title={
                            beforeUsage
                              ? `Avant le début d'utilisation du logiciel (${format(new Date(`${usageStart}T00:00:00`), 'dd/MM/yyyy')}) — rien n'est compté avant. Modifiable dans Paramètres.`
                              : before ? 'Pas encore embauché à cette date' : 'Contrat terminé à cette date'
                          }
                          className="h-full min-h-[42px] rounded border border-slate-100 bg-slate-50/60 flex items-center justify-center text-[10px] text-slate-300"
                        >
                          —
                        </div>
                      </div>
                    );
                  }

                  const weekday = date.getDay();
                  const worked = workedWeekdaysByEmp.get(emp.id);
                  const closed = closedByWeekday.get(weekday);
                  // Férié fermé = journée fermée, matin et après-midi.
                  const holiday = holidayMap.get(dayKey);
                  const holidayClosed = !!holiday && !holiday.isOpen;
                  const morningClosed = holidayClosed || !!closed?.morning;
                  const afternoonClosed = holidayClosed || !!closed?.afternoon;

                  // 1. Absence / indispo pleine journée — case EFFACÉE : le motif ne
                  //    subsiste que dans la pastille, pour que les présents dominent.
                  const absMeta = s?.absenceType
                    ? { label: absenceMeta(s.absenceType).label, dot: absenceMeta(s.absenceType).dot }
                    : (s ? STATUS_META[s.status] : undefined);
                  if (absMeta) {
                    return (
                      <div key={dayIdx} className="p-1 border-r border-slate-100">
                        <div title={absMeta.label} className={`h-full min-h-[42px] rounded border flex items-center justify-center gap-1 text-[10px] text-center px-1 ${ABSENCE_CELL_MUTED}`}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: absMeta.dot }} />
                          {absMeta.label}
                        </div>
                      </div>
                    );
                  }

                  // 2. Indisponible toute la journée.
                  if (isFullDayUnavail && unavail) {
                    const m = absenceMeta(unavail.type);
                    return (
                      <div key={dayIdx} className="p-1 border-r border-slate-100">
                        <div title={`Salarié indisponible toute la journée (${m.label}) — fiche › Disponibilités`} className={`h-full min-h-[42px] rounded border border-dashed flex items-center justify-center gap-1 text-[10px] ${ABSENCE_CELL_MUTED}`}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: m.dot }} />
                          <Ban size={10} className="flex-shrink-0" />{m.short}
                        </div>
                      </div>
                    );
                  }

                  // 3. Pharmacie fermée toute la journée — sauf si le salarié y
                  //    travaille quand même (M9-1 : ménage, magasinier, préparation
                  //    de commandes). Ses heures sont comptées, il doit se voir.
                  const worksWhileClosed = isWorkingDay(s);
                  if (morningClosed && afternoonClosed && !worksWhileClosed) {
                    return (
                      <div
                        key={dayIdx}
                        onDragOver={(e) => {
                          if (!canDropOn(emp.id, dayKey, s)) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'copy';
                          setDragOverKey(`${emp.id}|${dayKey}`);
                        }}
                        onDragLeave={(e) => leaveCell(e, `${emp.id}|${dayKey}`)}
                        onDrop={(e) => { e.preventDefault(); void handleDrop(emp.id, dayKey, format(date, 'EEEE d MMM', { locale: fr }), s); }}
                        onClick={() => openEditor(emp.id, `${emp.firstName} ${emp.lastName}`, dayKey, format(date, 'EEEE d MMM', { locale: fr }), s)}
                        className={`p-1 border-r border-slate-100 cursor-pointer transition-colors ${
                          canDropOn(emp.id, dayKey, s) && dragOverKey === `${emp.id}|${dayKey}`
                            ? 'bg-primary-bg ring-2 ring-inset ring-primary'
                            : 'hover:bg-primary-bg/40'
                        }`}
                      >
                        <div
                          title={
                            holidayClosed
                              ? `Férié, officine fermée (${holiday.name}) — cliquer pour y ajouter des heures quand même`
                              : 'Pharmacie fermée ce jour — cliquer pour y ajouter des heures quand même (ménage, magasinier…)'
                          }
                          className="h-full min-h-[42px] rounded border border-slate-200 bg-slate-100 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-slate-400"
                        >
                          <Ban size={12} />{holidayClosed ? 'Férié' : 'Fermé'}
                        </div>
                      </div>
                    );
                  }

                  // 4. Repos récurrent (non travaillé dans la semaine type).
                  const isRecurringRest = !!worked && !worked.has(weekday) && s?.status !== 'WORK';
                  if (isRecurringRest) {
                    // Un repos reste une cible de dépôt : recopier une journée
                    // dessus, c'est justement poser des heures sup / de récup.
                    const restLabel = format(date, 'EEEE d MMM', { locale: fr });
                    const restDrop = canDropOn(emp.id, dayKey, s);
                    const restOver = restDrop && dragOverKey === `${emp.id}|${dayKey}`;
                    return (
                      <div
                        key={dayIdx}
                        onDragOver={(e) => {
                          if (!restDrop) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'copy';
                          setDragOverKey(`${emp.id}|${dayKey}`);
                        }}
                        onDragLeave={(e) => leaveCell(e, `${emp.id}|${dayKey}`)}
                        onDrop={(e) => { e.preventDefault(); void handleDrop(emp.id, dayKey, restLabel, s); }}
                        className={`p-1 border-r border-slate-100 transition-colors ${restOver ? 'bg-primary-bg ring-2 ring-inset ring-primary' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => openEditor(emp.id, `${emp.firstName} ${emp.lastName}`, dayKey, restLabel, s, 'hours')}
                          title="Repos récurrent — cliquer pour ajouter des heures (sup / récup)"
                          className="group w-full h-full min-h-[42px] rounded border border-dashed border-slate-300 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-slate-500 hover:border-primary hover:text-primary" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0 4px, #f8fafc 4px 8px)' }}>
                          <CalendarOff size={12} className="group-hover:hidden" />
                          <Plus size={12} className="hidden group-hover:block" />
                          <span className="group-hover:hidden">Repos</span>
                          <span className="hidden group-hover:block">Ajouter</span>
                        </button>
                      </div>
                    );
                  }

                  // 5. Journée normale : deux demi-journées — cliquable pour éditer,
                  //    et attrapable pour recopier ses horaires sur un autre jour.
                  // Une journée basculée ailleurs est elle aussi « MANUAL » :
                  // elle n'a pas d'heures ici, le badge « sup » mentirait.
                  const isManual = s?.source === 'MANUAL' && !s.elsewhere;
                  // « Il ne vient pas samedi » : jour de semaine type retiré à
                  // la main, sans que ce soit une absence.
                  const isRemoved = isManual && s?.status === 'OFF';
                  const awaySite = s?.elsewhere
                    ? siteNameById.get(s.morningSiteId ?? s.afternoonSiteId ?? '') ?? 'une autre officine'
                    : null;
                  const dayLabel = format(date, 'EEEE d MMM', { locale: fr });
                  const grabbable = dragSlotsOf(s);
                  const dropTarget = canDropOn(emp.id, dayKey, s);
                  const isOver = dropTarget && dragOverKey === `${emp.id}|${dayKey}`;
                  return (
                    <div
                      key={dayIdx}
                      draggable={!!grabbable}
                      onDragStart={(e) => {
                        if (!grabbable) return;
                        e.dataTransfer.effectAllowed = 'copy';
                        // Firefox n'amorce pas le glissement sans charge utile.
                        e.dataTransfer.setData('text/plain', dayKey);
                        setDropFlash(null);
                        setDrag({ empId: emp.id, dateStr: dayKey, label: dayLabel, slots: grabbable });
                      }}
                      onDragEnd={() => { setDrag(null); setDragOverKey(null); }}
                      onDragOver={(e) => {
                        if (!dropTarget) return;
                        e.preventDefault(); // sans ça, le navigateur refuse le dépôt
                        e.dataTransfer.dropEffect = 'copy';
                        setDragOverKey(`${emp.id}|${dayKey}`);
                      }}
                      onDragLeave={(e) => leaveCell(e, `${emp.id}|${dayKey}`)}
                      onDrop={(e) => { e.preventDefault(); void handleDrop(emp.id, dayKey, dayLabel, s); }}
                      onClick={() => openEditor(emp.id, `${emp.firstName} ${emp.lastName}`, dayKey, dayLabel, s)}
                      title={
                        awaySite
                          ? `Journée à ${awaySite} — cliquer pour la basculer ou la rétablir`
                          : isRemoved
                          ? 'Journée retirée à la main — cliquer pour la rétablir'
                          : isManual
                          ? 'Heures ajoutées manuellement — cliquer pour déclarer une absence ou modifier'
                          : 'Cliquer pour déclarer une absence ou ajouter des heures'
                      }
                      className={`group/cell relative p-1 border-r border-slate-100 grid grid-cols-2 gap-1 cursor-pointer transition-colors ${
                        isOver ? 'bg-primary-bg ring-2 ring-inset ring-primary' : 'hover:bg-primary-bg/40'
                      } ${drag?.dateStr === dayKey && drag.empId === emp.id ? 'opacity-40' : ''} ${
                        drag && !dropTarget && drag.empId !== emp.id ? 'opacity-60' : ''
                      }`}
                    >
                      {grabbable && !drag && (
                        <div title="Glisser sur un autre jour pour y recopier ces horaires" className="absolute bottom-0 right-0 z-10 hidden group-hover/cell:block text-slate-400">
                          <GripVertical size={11} />
                        </div>
                      )}
                      {isRemoved ? (
                        <div title="Journée retirée à la main : il ne travaille pas ce jour-là" className="absolute top-0 left-0 z-10 rounded-br bg-slate-200 border-r border-b border-slate-300 px-1 py-0.5 text-[8px] font-semibold text-slate-600">
                          retiré
                        </div>
                      ) : isManual && (
                        <div title="Heures hors semaine type (sup / récup)" className="absolute top-0 left-0 z-10 rounded-br bg-accent/15 border-r border-b border-accent/30 px-1 py-0.5 text-[8px] font-semibold text-accent">
                          sup
                        </div>
                      )}
                      {isPartialUnavail && unavail && (
                        <div title={`Indisponible ${unavail.startTime}–${unavail.endTime} (${absenceMeta(unavail.type).short})`} className="absolute top-0 right-0 z-10 flex items-center gap-0.5 rounded-bl bg-amber-100 border-l border-b border-amber-300 px-1 py-0.5 text-[8px] font-medium text-amber-700 tabular-nums">
                          <Clock size={8} />{unavail.startTime}
                        </div>
                      )}
                      {(['morning', 'afternoon'] as Slot[]).map((slot) => {
                        const slotClosed = slot === 'morning' ? morningClosed : afternoonClosed;
                        // PRÉSENT = aplat plein : c'est ce bloc qui doit sauter aux yeux
                        // quand on parcourt la semaine (volume de personnel).
                        const isWork = s?.status === 'WORK' && (slot === 'morning' ? s.morningStart : s.afternoonStart);
                        // Multi-site : une journée peut se partager entre deux
                        // officines (« jusqu'à 16h à Saint-Pierre, puis 17h-20h
                        // au Palais »). L'API nous envoie la journée telle que
                        // CETTE officine la voit — l'autre moitié est vide — et
                        // dit où elle se passe. Une demi-journée faite ailleurs
                        // n'est pas un repos : on la nomme.
                        const halfSiteId = slot === 'morning' ? s?.morningSiteId : s?.afternoonSiteId;
                        const halfElsewhere =
                          !isWork && !!halfSiteId && !!activeSiteId && halfSiteId !== activeSiteId
                            ? siteNameById.get(halfSiteId) ?? 'autre officine'
                            : null;
                        // M9-1 — Une demi-journée fermée n'affiche « Fermé » que si
                        // PERSONNE n'y travaille : sinon on masquerait quelqu'un dont
                        // les heures sont bel et bien comptées (ménage, magasinier…).
                        if (slotClosed && !isWork) {
                          return (
                            <div key={slot} title={holidayClosed ? `Férié, officine fermée (${holiday.name})` : `Fermé ${slot === 'morning' ? 'le matin' : "l'après-midi"}`} className="rounded border border-slate-200 bg-slate-100 text-[9px] px-1.5 py-1.5 text-center font-medium text-slate-400 flex items-center justify-center">{holidayClosed ? 'Férié' : 'Fermé'}</div>
                          );
                        }
                        // Renfort pose automatiquement (horaires variables) : meme aplat,
                        // mais en pointilles pour qu'on voie que ce n'est pas un horaire habituel.
                        const isFill = isWork && s?.source === 'VARIABLE_FILL';
                        // M9-1 — Créneau qui déborde de l'ouverture : liseré ambre, pour
                        // qu'un horaire inattendu se voie sans être corrigé d'office.
                        const oh = openingByWeekday.get(weekday);
                        const outside =
                          !!isWork &&
                          (holidayClosed ||
                            isOutsideOpening(
                              slot === 'morning' ? s?.morningStart : s?.afternoonStart,
                              slot === 'morning' ? s?.morningEnd : s?.afternoonEnd,
                              slot === 'morning' ? oh?.morningOpen : oh?.afternoonOpen,
                              slot === 'morning' ? oh?.morningClose : oh?.afternoonClose
                            ));
                        const outsideTitle = holidayClosed
                          ? `Planifié un jour férié où l'officine est fermée (${holiday.name}) — heures comptées`
                          : slotClosed
                          ? `Travaille alors que la pharmacie est fermée ${slot === 'morning' ? 'le matin' : "l'après-midi"} — heures comptées`
                          : "Horaires hors des heures d'ouverture — heures comptées";
                        return (
                          <div
                            key={slot}
                            title={
                              halfElsewhere
                                ? `Cette demi-journée se fait à ${halfElsewhere}`
                                : outside
                                ? outsideTitle
                                : isFill
                                  ? 'Renfort ajoute automatiquement (horaires variables)'
                                  : undefined
                            }
                            // M9-2 — Aplat à la couleur du poste. Le renfort
                            // automatique garde le même aplat en pointillés :
                            // c'est le poste qu'on doit lire d'abord.
                            // Férié fermé : même un créneau posé reste gris, pour que
                            // la colonne se lise fermée d'un coup d'œil.
                            style={
                              isWork
                                ? holidayClosed
                                  ? { backgroundColor: '#e2e8f0', color: '#64748b', borderColor: '#cbd5e1' }
                                  : { backgroundColor: posStyle.bg, color: posStyle.text, borderColor: isFill ? 'rgba(255,255,255,.7)' : posStyle.bg }
                                : undefined
                            }
                            className={`rounded border text-[10px] px-1.5 py-1.5 text-left tabular-nums ${
                              isWork
                                ? isFill
                                  ? 'border-dashed font-semibold shadow-sm'
                                  : 'font-semibold shadow-sm'
                                : halfElsewhere
                                  ? 'bg-slate-100 text-slate-400 border-slate-200 font-medium truncate'
                                  : 'bg-slate-50/60 text-slate-300 border-slate-200 font-medium'
                            } ${outside ? 'ring-2 ring-amber-400 ring-inset' : ''}`}
                          >
                            {halfElsewhere ? `→ ${halfElsewhere}` : slotLabel(s, slot)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                <div className="px-2 flex items-center justify-center">
                  <div className="text-right">
                    <div className="text-[13px] font-semibold tabular-nums">{weekHours > 0 ? `${weekHours.toFixed(1)}h` : '—'}</div>
                    <div className="text-[9px] text-slate-400 tabular-nums">/ {emp.contractHours}h</div>
                  </div>
                </div>
              </div>
            );
          })}

          {(!employees || employees.length === 0) && (
            <div className="px-6 py-12 text-center text-[12px] text-slate-500">Aucun salarié actif. Ajoutez-en depuis l'onglet Salariés.</div>
          )}
        </div>

        {/* GARDES — sous la grille, jamais dedans : une garde traverse la nuit et
            s'ajoute à une journée déjà travaillée, elle n'a pas de case. */}
        <div className="edt-gardes">
          <DutyStrip from={fromIso} to={toIso} employees={employees ?? []} />
        </div>

        <div className="edt-legende mt-4 bg-white border border-slate-200 rounded-xl px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-2">Légende</div>
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-700">
            <span className="flex items-center gap-1.5">
              <span className="flex">
                {POSITION_LEGEND.map((p) => (
                  <span key={p.group} className="w-3 h-3 first:rounded-l-sm last:rounded-r-sm" style={{ backgroundColor: p.bg }} />
                ))}
              </span>
              <strong className="font-semibold">Présent</strong>
              <span className="text-slate-500">— la couleur dit le poste :</span>
            </span>
            {POSITION_LEGEND.map((p) => (
              <span key={p.group} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: p.bg }} />
                {p.group}
              </span>
            ))}
            <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm border ${ABSENCE_CELL_MUTED}`}></span>Absent (le motif est rappelé par la pastille)</span>
            <span className="flex items-center gap-1.5">
              {(['CONGE_PAYE', 'SICK', 'FORMATION'] as AbsenceType[]).map((t) => (
                <span key={t} className="w-2 h-2 rounded-full" style={{ backgroundColor: ABSENCE_META[t].dot }} />
              ))}
              <span className="text-slate-500">congé · arrêt · formation…</span>
            </span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300"></span>Férié ouvert</span>
            <span className="flex items-center gap-1.5" title="Paramètres → Jours fériés : « Ouvert ? Non »"><span className="w-3 h-3 rounded-sm bg-slate-200 border border-slate-300"></span>Férié fermé</span>
            <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm border border-dashed ${ABSENCE_CELL_MUTED}`}></span>Indisponible</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-dashed border-slate-300" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #e2e8f0 0 3px, #f8fafc 3px 6px)' }}></span>Repos récurrent</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-slate-300 bg-slate-100"></span>Fermé</span>
            <span className="flex items-center gap-1.5" title="Le planning ne rabote plus les horaires saisis sur les heures d'ouverture : ces heures sont comptées."><span className="w-3 h-3 rounded-sm bg-primary ring-2 ring-amber-400 ring-inset"></span>Hors heures d'ouverture</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-accent/15 border border-accent/40"></span>Heures ajoutées (sup / récup)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary/85 border border-dashed border-white/70"></span>Renfort automatique (horaires variables)</span>
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-3 text-[11px] text-slate-700">
            <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Couverture du jour</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Couverts</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Sous la cible</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500"></span>Sous le minimum</span>
          </div>
        </div>
       </div>
      </div>

      {fillOpen && variableStaff.data && (
        <VariableFillModal
          from={fromIso}
          to={toIso}
          staff={variableStaff.data}
          title="Combler les trous avec le personnel en horaires variables"
          reason="Seuls les creux sous l'effectif minimum sont comblés."
          onClose={() => setFillOpen(false)}
        />
      )}

      {editor && (
        <ShiftEditor
          key={`${editor.empId}|${editor.dateStr}`}
          editor={editor}
          saving={upsertMut.isPending}
          deleting={deleteMut.isPending}
          switching={switchSiteMut.isPending}
          sites={sitesData?.sites ?? []}
          activeSiteId={activeSiteId}
          serverError={editorError}
          onClose={() => { setEditorError(null); setEditor(null); }}
          onSave={async (slots) => {
            setEditorError(null);
            try {
              // `null` = « ne travaille pas ce jour » : une journée retirée à la
              // main, que la régénération ne remet pas.
              await upsertMut.mutateAsync({
                employeeId: editor.empId,
                date: editor.dateStr,
                ...(slots
                  ? { status: 'WORK', ...slots }
                  : { status: 'OFF', morningStart: null, morningEnd: null, afternoonStart: null, afternoonEnd: null }),
              });
              setEditor(null);
            } catch (e) {
              // La fenêtre RESTE ouverte, avec la raison. Elle se fermait sur
              // un succès et ne disait rien sur un échec : les deux cas se
              // ressemblaient trop pour qu'on sache lequel s'était produit.
              setEditorError(e instanceof Error ? e.message : "Enregistrement impossible.");
            }
          }}
          onDelete={async () => {
            setEditorError(null);
            try {
              await deleteMut.mutateAsync({ employeeId: editor.empId, date: editor.dateStr });
              setEditor(null);
            } catch (e) {
              setEditorError(e instanceof Error ? e.message : "Suppression impossible.");
            }
          }}
          onSwitchSite={async (morningSiteId, afternoonSiteId) => {
            setEditorError(null);
            try {
              const res = await switchSiteMut.mutateAsync({
                employeeId: editor.empId,
                date: editor.dateStr,
                morningSiteId,
                afternoonSiteId,
              });
              const names = [...new Set([morningSiteId, afternoonSiteId].filter(Boolean))]
                .map((id) => siteNameById.get(id!) ?? 'autre officine')
                .join(' / ');
              const added = res.addedSiteIds.map((id) => siteNameById.get(id) ?? 'autre officine');
              setDropFlash({
                kind: 'ok',
                text:
                  `${editor.empName} : ${editor.label} à ${names}, horaires inchangés.` +
                  (added.length > 0
                    ? ` ${editor.empName} apparaît désormais aussi dans le planning de ${added.join(', ')} (réglable dans sa fiche).`
                    : ''),
              });
              setEditor(null);
            } catch (e) {
              setEditorError(e instanceof Error ? e.message : 'Bascule impossible.');
            }
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ÉDITEUR DE CASE — déclarer une absence, ou ajouter des heures hors semaine type
// ============================================================================
// Le titulaire saisit l'absence SANS quitter le planning : plus d'aller-retour
// vers la fiche du salarié › Disponibilités. On tape les mêmes API que là-bas —
// période → table Absence (compte en CP / navette), plage horaire → indispo.
interface ShiftSlots {
  morningStart: string | null;
  morningEnd: string | null;
  afternoonStart: string | null;
  afternoonEnd: string | null;
}

type EditorTab = 'absence' | 'hours' | 'site';
type AbsenceScope = 'period' | 'range';

/**
 * Demi-journée vide. « 00:00 – 00:00 » compte comme vide : c'est ce que le
 * titulaire tape pour « effacer » ses heures, et le formulaire le refusait
 * (« Chaque demi-journée : début ET fin… ») au lieu de comprendre.
 */
const isBlankHalf = (a: string, b: string) => (!a && !b) || (a === '00:00' && b === '00:00');

function ShiftEditor({
  editor, saving, deleting, switching, sites, activeSiteId, serverError, onClose, onSave, onDelete, onSwitchSite,
}: {
  editor: { empId: string; empName: string; dateStr: string; label: string; s?: Schedule; defaultTab: EditorTab; multiSite: boolean };
  saving: boolean;
  deleting: boolean;
  switching: boolean;
  /** Les officines du groupe : l'onglet « Officine » n'apparaît qu'à partir de deux. */
  sites: Site[];
  activeSiteId?: string;
  /** Refus du serveur (créneau bloqué, salarié hors officine…) — à afficher tel quel. */
  serverError: string | null;
  onClose: () => void;
  /** `null` = la journée n'est pas travaillée (retirée à la main, sans motif d'absence). */
  onSave: (slots: ShiftSlots | null) => void;
  onDelete: () => void;
  onSwitchSite: (morningSiteId?: string, afternoonSiteId?: string) => void;
}) {
  const s = editor.s;
  const multiSiteGroup = sites.length > 1;
  const [tab, setTab] = useState<EditorTab>(
    editor.defaultTab === 'site' && !multiSiteGroup ? 'absence' : editor.defaultTab
  );
  const [mStart, setMStart] = useState(s?.morningStart ?? '');
  const [mEnd, setMEnd] = useState(s?.morningEnd ?? '');
  const [aStart, setAStart] = useState(s?.afternoonStart ?? '');
  const [aEnd, setAEnd] = useState(s?.afternoonEnd ?? '');
  const [error, setError] = useState<string | null>(null);

  const isManual = s?.source === 'MANUAL';
  const isFill = s?.source === 'VARIABLE_FILL';
  const hasHoursHere = s?.status === 'WORK' && !!(s.morningStart || s.afternoonStart);
  const mBlank = isBlankHalf(mStart, mEnd);
  const aBlank = isBlankHalf(aStart, aEnd);
  const noHours = mBlank && aBlank;
  // Vider les heures d'une journée qui en avait = la retirer. Sur une case
  // déjà vide (repos), il n'y a rien à retirer : on demande des heures.
  const removing = noHours && hasHoursHere;

  function save() {
    const halfOk = (a: string, b: string) => isBlankHalf(a, b) || (!!a && !!b && a < b);
    if (!halfOk(mStart, mEnd) || !halfOk(aStart, aEnd)) {
      setError('Chaque demi-journée : début ET fin, et fin après début — ou les deux cases vides.');
      return;
    }
    setError(null);
    // Aucune heure : la journée n'est pas travaillée. Avant, c'était un refus
    // (« Indiquez au moins une demi-journée ») et il n'existait aucun moyen de
    // retirer UN jour de semaine type sans passer par une absence.
    if (noHours) {
      if (removing) onSave(null);
      else setError('Indiquez au moins une demi-journée.');
      return;
    }
    onSave({
      morningStart: mBlank ? null : mStart,
      morningEnd: mBlank ? null : mEnd,
      afternoonStart: aBlank ? null : aStart,
      afternoonEnd: aBlank ? null : aEnd,
    });
  }

  // Bouton de gauche : défaire ce qui a été posé à la main, ou retirer une
  // journée que le titulaire n'a pas posée. Un renfort « Combler les trous »
  // n'avait AUCUN bouton : une fois posé, il ne se retirait plus.
  let leftAction: React.ReactNode = <span />;
  if (isManual) {
    const restoring = s?.status !== 'WORK' || !!s?.elsewhere;
    leftAction = (
      <Button
        variant={restoring ? 'default' : 'danger'}
        size="sm"
        icon={restoring ? RotateCcw : Trash2}
        loading={deleting}
        onClick={onDelete}
        title="Annule cette saisie : la journée revient à son horaire habituel (semaine type)"
      >
        {restoring ? 'Rétablir' : 'Retirer'}
      </Button>
    );
  } else if (hasHoursHere) {
    leftAction = (
      <Button
        variant="danger"
        size="sm"
        icon={Trash2}
        loading={saving}
        onClick={() => onSave(null)}
        title={
          isFill
            ? "Retire ce renfort : il ne revient pas au prochain « Combler les trous »"
            : 'Ce jour-là seulement, il ne travaille pas — la semaine type ne bouge pas'
        }
      >
        {isFill ? 'Retirer ce renfort' : 'Retirer la journée'}
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        <h2 className="text-[15px] font-semibold text-slate-900">{editor.empName}</h2>
        <p className="text-[12px] text-slate-500 capitalize mb-4">{editor.label}</p>

        <div className={`grid ${multiSiteGroup ? 'grid-cols-3' : 'grid-cols-2'} gap-1 bg-slate-100 rounded-lg p-0.5 mb-4`}>
          <button
            type="button"
            onClick={() => setTab('absence')}
            className={`py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === 'absence' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Absence
          </button>
          <button
            type="button"
            onClick={() => setTab('hours')}
            className={`py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === 'hours' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Heures
          </button>
          {/* « Basculer à la pharmacie… » : une inversion d'UNE journée, sans
              passer par la semaine type « Global multi-site ». */}
          {multiSiteGroup && (
            <button
              type="button"
              onClick={() => setTab('site')}
              className={`py-1.5 rounded-md text-[12px] font-medium transition-colors ${tab === 'site' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Officine
            </button>
          )}
        </div>

        {tab === 'absence' ? (
          <AbsenceQuickForm
            empId={editor.empId}
            dateStr={editor.dateStr}
            onDone={onClose}
          />
        ) : tab === 'site' ? (
          <SiteSwitchForm
            s={s}
            sites={sites}
            activeSiteId={activeSiteId}
            switching={switching}
            deleting={deleting}
            serverError={serverError}
            onSwitch={onSwitchSite}
            onRestore={onDelete}
            onCancel={onClose}
          />
        ) : (
        <>
        <div className="space-y-3">
          <div>
            <div className="text-[11px] font-medium text-slate-500 mb-1">Matin</div>
            <div className="flex items-center gap-2">
              <input type="time" value={mStart} onChange={(e) => setMStart(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[13px]" />
              <span className="text-slate-400">–</span>
              <input type="time" value={mEnd} onChange={(e) => setMEnd(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[13px]" />
            </div>
          </div>
          <div>
            <div className="text-[11px] font-medium text-slate-500 mb-1">Après-midi</div>
            <div className="flex items-center gap-2">
              <input type="time" value={aStart} onChange={(e) => setAStart(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[13px]" />
              <span className="text-slate-400">–</span>
              <input type="time" value={aEnd} onChange={(e) => setAEnd(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-[13px]" />
            </div>
          </div>
        </div>

        {removing ? (
          <p className="text-[11px] text-slate-500 mt-3 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
            Aucune heure : la journée sera <strong>non travaillée</strong>, ce jour-là seulement. Ce
            n'est pas une absence — aucun motif, rien n'est décompté en congé. Pour une absence,
            onglet « Absence ».
          </p>
        ) : (
          <p className="text-[11px] text-slate-400 mt-3">
            Ces heures sont comptées et ne sont pas écrasées par la régénération du planning.
          </p>
        )}
        {editor.multiSite && (
          <p className="text-[11px] text-slate-500 mt-2 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
            Salarié partagé : ces heures le placent dans <strong>l'officine affichée</strong> ce
            jour-là. S'il y était attendu dans l'autre officine du groupe, il en sortira — un
            salarié n'est que dans une officine à la fois.
            {multiSiteGroup && <> Pour l'envoyer ailleurs sans retaper ses heures : onglet « Officine ».</>}
          </p>
        )}
        {error && <p className="text-[12px] text-danger mt-2">{error}</p>}
        {serverError && (
          <p className="text-[12px] text-danger mt-2 rounded-lg bg-danger/5 border border-danger/20 px-2 py-1.5">
            {serverError}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 mt-5">
          {leftAction}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
            <Button variant="primary" size="sm" loading={saving} onClick={save}>
              {removing ? 'Ne travaille pas' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// « Basculer à la pharmacie… » — une journée, une autre officine
// ----------------------------------------------------------------------------
// « Quand tu veux changer quelqu'un de pharmacie tu ne peux pas le faire si
//   c'est juste une inversion pour une journée. T'es obligé de passer par le
//   changer les horaires global. »
//
// Les horaires ne bougent pas : on dit seulement OÙ se fait chaque moitié de la
// journée. Le serveur relit la journée entière — y compris une moitié faite
// ailleurs, dont les heures ne nous parviennent pas ici.
function SiteSwitchForm({
  s, sites, activeSiteId, switching, deleting, serverError, onSwitch, onRestore, onCancel,
}: {
  s?: Schedule;
  sites: Site[];
  activeSiteId?: string;
  switching: boolean;
  deleting: boolean;
  serverError: string | null;
  onSwitch: (morningSiteId?: string, afternoonSiteId?: string) => void;
  onRestore: () => void;
  onCancel: () => void;
}) {
  const half = (slot: Slot) => {
    const start = slot === 'morning' ? s?.morningStart : s?.afternoonStart;
    const end = slot === 'morning' ? s?.morningEnd : s?.afternoonEnd;
    const siteId = (slot === 'morning' ? s?.morningSiteId : s?.afternoonSiteId) ?? activeSiteId ?? '';
    // Travaillée ici (on en voit les heures), ou ailleurs (l'API dit où).
    const worked = s?.status === 'WORK' && (!!start || (!!siteId && siteId !== activeSiteId));
    return { hours: start && end ? `${start}–${end}` : null, siteId, worked };
  };
  const m = half('morning');
  const a = half('afternoon');
  const [mSite, setMSite] = useState(m.siteId);
  const [aSite, setASite] = useState(a.siteId);
  const changed = (m.worked && mSite !== m.siteId) || (a.worked && aSite !== a.siteId);
  const siteName = (id: string) => sites.find((x) => x.id === id)?.name ?? 'autre officine';

  if (!m.worked && !a.worked) {
    return (
      <div className="text-[12px] text-slate-600 space-y-2">
        <p>
          Pas d'heures ce jour-là à déplacer.
        </p>
        <p className="text-[11px] text-slate-500">
          Pour faire venir ce salarié dans une autre officine un jour sans heures : passez sur le
          planning de cette officine (sélecteur en haut de l'écran) et saisissez ses heures dans
          l'onglet « Heures ».
        </p>
        <div className="flex justify-end mt-4">
          <Button variant="ghost" size="sm" onClick={onCancel}>Fermer</Button>
        </div>
      </div>
    );
  }

  const halfRow = (label: string, h: ReturnType<typeof half>, value: string, set: (v: string) => void) => (
    <div className="flex items-center gap-2">
      <div className="w-24 flex-shrink-0">
        <div className="text-[11px] font-medium text-slate-600">{label}</div>
        <div className="text-[10.5px] text-slate-400 tabular-nums">
          {h.hours ?? `à ${siteName(h.siteId)}`}
        </div>
      </div>
      <select value={value} onChange={(e) => set(e.target.value)} className="input flex-1 text-[12px]">
        {sites.map((site) => (
          <option key={site.id} value={site.id}>{site.name}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <p className="text-[11.5px] text-slate-600 mb-3">
        Pour <strong>ce jour-là seulement</strong>, horaires inchangés. La semaine type, elle, ne
        bouge pas.
      </p>

      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">
        Toute la journée à
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sites.map((site) => {
          const all = (!m.worked || mSite === site.id) && (!a.worked || aSite === site.id);
          return (
            <button
              key={site.id}
              type="button"
              onClick={() => { setMSite(site.id); setASite(site.id); }}
              className={`px-2.5 py-1.5 rounded-lg border text-[12px] font-medium transition-colors ${
                all
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-primary'
              }`}
            >
              {site.name}
            </button>
          );
        })}
      </div>

      {m.worked && a.worked && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mt-4 mb-1.5">
            Ou demi-journée par demi-journée
          </div>
          <div className="space-y-2">
            {halfRow('Matin', m, mSite, setMSite)}
            {halfRow('Après-midi', a, aSite, setASite)}
          </div>
        </>
      )}

      {serverError && (
        <p className="text-[12px] text-danger mt-3 rounded-lg bg-danger/5 border border-danger/20 px-2 py-1.5">
          {serverError}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 mt-5">
        {s?.source === 'MANUAL' ? (
          <Button
            variant="default"
            size="sm"
            icon={RotateCcw}
            loading={deleting}
            onClick={onRestore}
            title="La journée revient à son officine et à ses horaires habituels (semaine type)"
          >
            Rétablir
          </Button>
        ) : <span />}
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Annuler</Button>
          <Button
            variant="primary"
            size="sm"
            icon={ArrowLeftRight}
            loading={switching}
            disabled={!changed}
            onClick={() => onSwitch(m.worked ? mSite : undefined, a.worked ? aSite : undefined)}
          >
            Basculer
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Saisie rapide d'absence depuis le planning
// ----------------------------------------------------------------------------
function AbsenceQuickForm({
  empId, dateStr, onDone,
}: {
  empId: string;
  dateStr: string;
  onDone: () => void;
}) {
  const [scope, setScope] = useState<AbsenceScope>('period');
  const [startDate, setStartDate] = useState(dateStr);
  const [endDate, setEndDate] = useState(dateStr);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createAbsence = useCreateAbsence();
  const setUnavailability = useSetUnavailability(empId);
  const pending = createAbsence.isPending || setUnavailability.isPending;

  async function apply(type: AbsenceType) {
    setError(null);
    try {
      if (scope === 'period') {
        if (!startDate || !endDate || startDate > endDate) {
          setError('La date de fin doit être après la date de début.');
          return;
        }
        await createAbsence.mutateAsync({
          employeeId: empId,
          type,
          startDate,
          endDate,
          comment: comment || undefined,
        });
      } else {
        if (startTime >= endTime) {
          setError("L'heure de fin doit être après l'heure de début.");
          return;
        }
        await setUnavailability.mutateAsync({ date: dateStr, type, startTime, endTime });
      }
      onDone();
    } catch (e) {
      setError((e as Error).message ?? "Échec de l'enregistrement");
    }
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-0.5 mb-3">
        <button
          type="button"
          onClick={() => setScope('period')}
          className={`py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${scope === 'period' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Journée(s) entière(s)
        </button>
        <button
          type="button"
          onClick={() => setScope('range')}
          className={`py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${scope === 'range' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Plage horaire
        </button>
      </div>

      {scope === 'period' ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Du</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Au</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
            </div>
          </div>
          <div className="mt-2">
            <label className="label">Commentaire <span className="text-slate-400 font-normal">(facultatif)</span></label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Justificatif ? Remplaçant ?" className="input" />
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Début</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input tabular-nums text-center" />
          </div>
          <div>
            <label className="label">Fin</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input tabular-nums text-center" />
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
        Motif — cliquez pour enregistrer
      </div>
      <div className="mt-1 -mx-1">
        {ABSENCE_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => apply(t)}
            disabled={pending}
            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-left text-[12.5px] hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ABSENCE_META[t].dot }} />
            <span className="flex-1 text-slate-700">{ABSENCE_META[t].label}</span>
          </button>
        ))}
      </div>

      {error && <p className="text-[12px] text-danger mt-2">{error}</p>}
      <p className="text-[11px] text-slate-400 mt-3">
        {scope === 'period'
          ? 'Le planning, la couverture et la fiche navette se mettent à jour automatiquement.'
          : "Seules ces heures sont retirées du planning ; le reste de la journée est conservé."}
      </p>
    </div>
  );
}
